import type { EngagementResult, ScenarioInput, ScenarioResult } from '@exigo/shared';
import { formatMoney } from '@exigo/shared';

/**
 * Builds the export document — the artefact that goes to the PDM.
 *
 * Produced in the browser from the result the API already returned. That is not
 * a breach of the engine-stays-server-side rule: nothing here calculates
 * anything. Every figure is read straight off `ScenarioResult`, which the
 * calculator is already displaying on screen.
 *
 * Written as a self-contained document with inline styles and no external
 * assets, because it has to survive three very different renderers: the print
 * dialog, Word, and LibreOffice. Table shading is duplicated as a legacy
 * `bgcolor` attribute for the same reason — Word and LibreOffice drop CSS
 * backgrounds on table cells.
 */

export interface ExportOptions {
  result: ScenarioResult;
  input: ScenarioInput;
  areaName: string;
  customerName: string | null;
  guideLabel: string;
  /** Rendered into the document, so the reader knows when it was produced. */
  preparedOn: Date;
  /** Mirrors the legacy "Include near-miss list" checkbox. */
  includeNearMiss: boolean;
}

const INK = '#14161f';
const MUT = '#5b6070';
const RULE = '#d9d5ee';
const NAVY = '#101a33';
const GOLD = '#b8912a';
const SHADE = '#f2f0fa';

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function longDate(d: Date): string {
  return `${String(d.getDate())} ${MONTHS[d.getMonth()] ?? ''} ${String(d.getFullYear())}`;
}

/** Slug for the download filename: `Exigo-MCI-Cloud-AI-Platform-2026-08-26.doc`. */
export function exportFileName(areaName: string, preparedOn: Date, extension: string): string {
  const slug = areaName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const y = preparedOn.getFullYear();
  const m = String(preparedOn.getMonth() + 1).padStart(2, '0');
  const d = String(preparedOn.getDate()).padStart(2, '0');
  return `Exigo-MCI-${slug}-${String(y)}-${m}-${d}.${extension}`;
}

const PHASE_LABEL: Record<string, string> = {
  pre: 'Pre-sales',
  post: 'Post-sales',
  bonus: 'Conversion bonus',
  smb: 'Post-sales (SMB track)',
  usage: 'Usage accelerator',
  stage2: 'Post-sales (two-stage)',
};

function phaseText(phase: string): string {
  return PHASE_LABEL[phase] ?? phase;
}

export function buildExportHtml(options: ExportOptions): string {
  const { result, input, areaName, customerName, guideLabel, preparedOn, includeNearMiss } = options;

  const fx = input.fx;
  const aud = (usd: number | null): string =>
    fx > 0 && usd !== null ? formatMoney(Math.round((usd * fx) / 1_000_000)) : '—';
  const usdOf = (v: number | null): string => (v === null ? '—' : formatMoney(v));

  const byKey = new Map(result.rows.map((r) => [r.key, r]));
  const recommended = result.rows.filter((r) => r.eligible && r.pick === 'recommended');
  const stackable = result.rows.filter((r) => r.eligible && r.pick === null);
  const alternatives = result.rows.filter((r) => r.eligible && r.pick === 'alternative');
  const nearMiss = result.oneCapabilityAwayKeys
    .map((k) => byKey.get(k))
    .filter((r): r is EngagementResult => r !== undefined);

  const toNominate = [...recommended, ...stackable];
  const detailed = [...recommended, ...stackable, ...alternatives];

  const fxLine =
    fx > 0
      ? `Converted at ${(fx / 1_000_000).toFixed(4)} AUD per USD.`
      : 'No AUD conversion rate has been set, so AUD columns show a dash.';

  const parts: string[] = [];

  // --- Hero ---------------------------------------------------------------
  parts.push(`
<div class="hero">
  <div class="kicker">Exigo Tech &middot; Internal &middot; Microsoft Confidential</div>
  <h1>${esc(areaName)} &mdash; claimable engagements</h1>
  <div class="herosub">Microsoft Commerce Incentives, ${esc(guideLabel)}${
    customerName ? ` &middot; ${esc(customerName)}` : ''
  } &middot; prepared ${esc(longDate(preparedOn))}</div>
</div>`);

  // --- What this is -------------------------------------------------------
  parts.push(`
<h2>What this is</h2>
<p class="lede">
  ${
    toNominate.length > 0
      ? 'Every engagement below clears all eligibility gates on the capability position recorded for Exigo Tech and the deal figures entered.'
      : 'No engagement in this solution area clears every gate on the figures entered.'
  }
  Amounts are fees Microsoft pays Exigo for delivering the engagement. They are not discounts,
  rebates or funding for the customer and must not be quoted to a customer or included in a
  proposal. Sizing bands are set in USD. ${esc(fxLine)}
</p>`);

  // --- Deal basis ---------------------------------------------------------
  const basisRows = result.contractValueLines
    .map(
      (line) => `
    <tr>
      <td class="k">${esc(line.label)}, 12 months</td>
      <td>${line.known ? `${usdOf(line.usd)} USD &middot; ${aud(line.usd)} AUD` : 'not entered'}${
        line.note ? `<div class="note">${esc(line.note)}</div>` : ''
      }</td>
    </tr>`,
    )
    .join('');

  parts.push(`
<h2>Deal basis</h2>
<table class="kv">
  ${basisRows}
  <tr><td class="k">Customer segment</td><td>${esc(input.segment)}</td></tr>
  <tr><td class="k">Payment market</td><td>Market ${esc(input.market)}</td></tr>
  <tr><td class="k">Contract term considered</td><td>${String(input.termMonths)} months
    (Microsoft Commerce Incentives earns on year 1 only)</td></tr>
</table>`);

  // --- Result -------------------------------------------------------------
  if (toNominate.length === 0) {
    parts.push(`
<h2>Result</h2>
<p>No engagement in this solution area clears every gate on the figures entered. Nothing is
claimable here as scoped.</p>`);
  } else {
    parts.push(`
<h2>Recommended nomination</h2>
<p class="big">${usdOf(result.claimableTotalUsd)} USD &middot; ${aud(result.claimableTotalUsd)} AUD</p>
<p class="note">One pre-sales and one post-sales nomination per project, plus any stacking bonus —
not every eligible engagement. Alternatives measure the same consumption and are listed separately.</p>

<h2>What to nominate</h2>
<table class="grid">
  <tr class="hd">
    <th bgcolor="${SHADE}">Engagement</th><th bgcolor="${SHADE}">Role</th>
    <th bgcolor="${SHADE}">Type</th><th bgcolor="${SHADE}">Band</th>
    <th bgcolor="${SHADE}" class="r">USD</th><th bgcolor="${SHADE}" class="r">AUD</th>
  </tr>
  ${toNominate
    .map(
      (r) => `
  <tr>
    <td>${esc(r.name)}</td>
    <td>${r.pick === 'recommended' ? 'Nominate' : 'Stacks on top'}</td>
    <td>${esc(phaseText(r.phase))}</td>
    <td>${esc(r.tier?.size ?? '—')}</td>
    <td class="r">${usdOf(r.payoutUsd)}</td>
    <td class="r">${aud(r.payoutUsd)}</td>
  </tr>`,
    )
    .join('')}
  <tr class="tot">
    <td colspan="4" bgcolor="${SHADE}"><b>Total to claim</b></td>
    <td class="r" bgcolor="${SHADE}"><b>${usdOf(result.claimableTotalUsd)}</b></td>
    <td class="r" bgcolor="${SHADE}"><b>${aud(result.claimableTotalUsd)}</b></td>
  </tr>
</table>`);

    if (result.recommendationReasons.length > 0) {
      parts.push(`
<div class="callout">
  <div class="calloutTitle">Why this engagement</div>
  <ul>${result.recommendationReasons.map((r) => `<li>${esc(r)}</li>`).join('')}</ul>
</div>`);
    }
  }

  // --- Alternatives -------------------------------------------------------
  if (alternatives.length > 0) {
    parts.push(`
<h2>Alternatives &mdash; same consumption, not additional</h2>
<p class="note">These clear every gate but measure the same incremental consumption as the
recommendation. One post-sales and one pre-sales nomination per project. Verify with your PDM
before assuming any two can be claimed together.</p>
<table class="grid">
  <tr class="hd">
    <th bgcolor="${SHADE}">Engagement</th><th bgcolor="${SHADE}">Type</th>
    <th bgcolor="${SHADE}">Band</th><th bgcolor="${SHADE}" class="r">USD</th>
    <th bgcolor="${SHADE}" class="r">AUD</th>
  </tr>
  ${alternatives
    .map(
      (r) => `
  <tr>
    <td>${esc(r.name)}</td><td>${esc(phaseText(r.phase))}</td>
    <td>${esc(r.tier?.size ?? '—')}</td>
    <td class="r">${usdOf(r.payoutUsd)}</td><td class="r">${aud(r.payoutUsd)}</td>
  </tr>`,
    )
    .join('')}
</table>`);
  }

  // --- Per-engagement detail ---------------------------------------------
  if (detailed.length > 0) {
    parts.push('<h2>Engagement detail and eligibility criteria</h2>');
    detailed.forEach((r, i) => {
      const held = r.gates.filter((g) => g.ok);
      parts.push(`
<div class="eng">
  <h3>${String(i + 1)}. ${esc(r.name)}</h3>
  <table class="kv">
    <tr><td class="k">Engagement type</td><td>${esc(phaseText(r.phase))}</td></tr>
    <tr><td class="k">Band</td><td>${esc(r.tier?.size ?? '—')}${
      r.tier?.minHours ? ` &middot; minimum ${String(r.tier.minHours)} delivery hours` : ''
    }</td></tr>
    <tr><td class="k">Partner payment</td><td>${usdOf(r.payoutUsd)} USD &middot; ${aud(
      r.payoutUsd,
    )} AUD${
      r.stageTwoUsd
        ? `<div class="note">Plus ${usdOf(r.stageTwoUsd)} USD stage 2, conditional on ACR attainment.</div>`
        : ''
    }</td></tr>
    ${
      r.yieldPct !== null
        ? `<tr><td class="k">As % of 12-month value</td><td>${r.yieldPct.toFixed(1)}%</td></tr>`
        : ''
    }
    ${
      r.perHourUsd !== null
        ? `<tr><td class="k">Per minimum delivery hour</td><td>${usdOf(r.perHourUsd)} USD</td></tr>`
        : ''
    }
    ${r.duration ? `<tr><td class="k">Duration</td><td>${esc(r.duration)}</td></tr>` : ''}
    <tr><td class="k">Qualifying workloads</td><td>${esc(r.workloads)}</td></tr>
  </table>

  <div class="qual">
    <div class="calloutTitle">Why Exigo qualifies.</div>
    <ul>${held.map((g) => `<li><b>${esc(g.label)}</b> &mdash; ${esc(g.detail)}</li>`).join('')}</ul>
  </div>

  ${
    r.poe.length > 0
      ? `<div class="sub">Proof of execution required</div>
         <ul>${r.poe.map((p) => `<li>${esc(p)}</li>`).join('')}</ul>`
      : ''
  }
  ${
    r.notes.length > 0
      ? `<div class="sub">Conditions and risks</div>
         <ul>${r.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>`
      : ''
  }
</div>`);
    });
  }

  // --- Near-miss list (opt-in) -------------------------------------------
  if (includeNearMiss && nearMiss.length > 0) {
    parts.push(`
<h2>Not claimable today &mdash; blocked only on partner capability</h2>
<p class="note">Every gate except partner capability already passes on these. This is the
quantified business case for finishing an enrolment.</p>
<table class="grid">
  <tr class="hd">
    <th bgcolor="${SHADE}">Engagement</th><th bgcolor="${SHADE}">Capability required</th>
    <th bgcolor="${SHADE}">Band</th><th bgcolor="${SHADE}" class="r">USD</th>
    <th bgcolor="${SHADE}" class="r">AUD</th>
  </tr>
  ${nearMiss
    .map(
      (r) => `
  <tr>
    <td>${esc(r.name)}</td>
    <td>${esc(r.capabilityDetail.join(', '))}</td>
    <td>${esc(r.tier?.size ?? '—')}</td>
    <td class="r">${usdOf(r.payoutUsd)}</td><td class="r">${aud(r.payoutUsd)}</td>
  </tr>`,
    )
    .join('')}
</table>`);
  }

  // --- Pre-qualification record (ABS and Security only) -------------------
  if (input.area === 'abs' || input.area === 'security') {
    const pq = input.prequalification;
    const yn = (v: boolean): string => (v ? 'Confirmed' : 'Not confirmed');
    const num = (v: number): string => (v === 0 ? 'not recorded' : v.toLocaleString('en-AU'));
    parts.push(`
<h2>Customer pre-qualification recorded</h2>
<p class="note">These engagements do not size on consumption. The conditions below were recorded
at the time this document was produced and each one is a live eligibility requirement, not
background. Re-verify before nominating.</p>
<table class="kv">
  <tr><td class="k">Customer type</td><td>${esc(pq.customerType)}</td></tr>
  <tr><td class="k">Microsoft 365 seats</td><td>${num(pq.m365Seats)}</td></tr>
  <tr><td class="k">Intune seats purchased</td><td>${num(pq.intuneSeats)}</td></tr>
  <tr><td class="k">Intune monthly active users</td><td>${num(pq.intuneMonthlyActive)}</td></tr>
  <tr><td class="k">Entra ID P1 usage</td><td>${
    pq.entraP1UsagePct === 0 ? 'not recorded' : `${String(pq.entraP1UsagePct)}%`
  }</td></tr>
  <tr><td class="k">Copilot paid available units</td><td>${num(pq.copilotPaidUnits)}</td></tr>
  <tr><td class="k">Copilot monthly active users</td><td>${num(pq.copilotMonthlyActive)}</td></tr>
  <tr><td class="k">E5 security paid units</td><td>${num(pq.e5PaidUnits)}</td></tr>
  <tr><td class="k">Monthly protected users deployed</td><td>${num(
    pq.monthlyProtectedUsersDeployed,
  )}</td></tr>
  <tr><td class="k">FY26 security workshops claimed</td><td>${String(
    pq.fy26SecurityWorkshops,
  )}</td></tr>
  <tr><td class="k">CSP annual term, 6+ months remaining</td><td>${yn(pq.cspTermsOk)}</td></tr>
  <tr><td class="k">Net paid seat adds, same tenant and month</td><td>${yn(
    pq.netPaidSeatAdds,
  )}</td></tr>
  <tr><td class="k">Nominated pre-sale or within two months</td><td>${yn(pq.timingOk)}</td></tr>
  <tr><td class="k">Within approved and concurrent claim caps</td><td>${yn(
    pq.withinClaimCaps,
  )}</td></tr>
  <tr><td class="k">On GTM pre-published propensity list</td><td>${yn(
    pq.onPropensityList,
  )}</td></tr>
  <tr><td class="k">Non-unified EA, commercial E5, FY27/FY28 renewal</td><td>${yn(
    pq.eaE5Renewal,
  )}</td></tr>
  <tr><td class="k">MXDR verified practice</td><td>${pq.mxdrVerified ? 'Held' : 'Not held'}</td></tr>
  <tr><td class="k">MISA member</td><td>${pq.misaMember ? 'Held' : 'Not held'}</td></tr>
</table>`);
  }

  // --- Constraints, confidentiality, footer -------------------------------
  parts.push(`
<h2>Constraints that apply to every claim</h2>
<ul>
  <li><b>MCI earns on year 1 only.</b> A three-year contract does not triple the incentive.</li>
  <li><b>Azure pre-sales to post-sales ratio is capped at 2:1.</b> Exceed it and pre-sales claims
    are paused after 30 days' notice.</li>
  <li><b>Azure post-sales needs a 60% success rate.</b> Months 4&ndash;6 need 75% of minimum,
    months 7&ndash;12 need 100%.</li>
  <li><b>Regional partner earning caps:</b> USD 800K for SMB customers, USD 2M total pre-sales.</li>
  <li><b>Security claims:</b> partner-wide limit of 50 concurrent active claims.</li>
  <li><b>Customer consent expires.</b> 30 days to obtain consent, 90 to submit POE, 30 for
    validation, payment up to 45 days after the claim approval month end.</li>
  <li><b>Azure credits and cost-optimisation work reduce measured ACR.</b></li>
  <li><b>Subscription IDs can be corrected once</b> per claim.</li>
</ul>

<div class="conf">
  <b>Confidentiality.</b> This document reproduces Microsoft Confidential material. Share it only
  with people covered by Exigo's Microsoft AI Cloud Partner Program Agreement — not customers,
  subcontractors or distributors.
</div>

<div class="foot">
  Exigo Tech Pty Ltd &middot; ABN 47 607 270 313 &middot; Sydney &middot; Melbourne &middot;
  Brisbane &middot; Auckland &middot; Singapore &middot; Manila<br />
  Generated ${esc(longDate(preparedOn))} from ${esc(guideLabel)}. ${esc(fxLine)}
  Verify t-shirt band and eligibility in Partner Center before nominating — where this document and
  Partner Center disagree, Partner Center governs.
</div>`);

  return `<!DOCTYPE html>
<html lang="en-AU"><head><meta charset="utf-8" />
<title>${esc(areaName)} — claimable engagements</title>
<style>
  @page { size: A4; margin: 14mm 13mm; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: ${INK}; font-size: 10.5pt;
         line-height: 1.5; margin: 0; }
  .hero { background: ${NAVY}; color: #fff; padding: 16px 18px; margin-bottom: 18px;
          border-bottom: 3px solid ${GOLD}; -webkit-print-color-adjust: exact;
          print-color-adjust: exact; }
  .kicker { color: ${GOLD}; font-size: 8pt; font-weight: 700; letter-spacing: .12em;
            text-transform: uppercase; }
  .hero h1 { font-size: 20pt; margin: 6px 0 4px; font-weight: 700; }
  .herosub { font-size: 9pt; color: #c9cede; }
  h2 { font-size: 13pt; margin: 20px 0 8px; border-bottom: 1px solid ${RULE};
       padding-bottom: 4px; page-break-after: avoid; }
  h3 { font-size: 11pt; margin: 16px 0 6px; page-break-after: avoid; }
  p { margin: 0 0 8px; }
  .lede { color: ${MUT}; }
  .note { color: ${MUT}; font-size: 9pt; }
  .big { font-size: 17pt; font-weight: 700; }
  .sub { font-size: 9pt; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
         color: ${MUT}; margin-top: 10px; }
  table { border-collapse: collapse; width: 100%; margin: 6px 0 10px; page-break-inside: avoid; }
  .kv td { padding: 5px 8px; border-bottom: 1px solid ${RULE}; vertical-align: top; }
  .kv td.k { color: ${MUT}; width: 38%; }
  .grid th, .grid td { padding: 6px 8px; border: 1px solid ${RULE}; text-align: left;
                       font-size: 9.5pt; }
  .grid th { background: ${SHADE}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .r { text-align: right; }
  ul { margin: 4px 0 8px; padding-left: 18px; }
  li { margin-bottom: 3px; }
  .callout, .qual { border-left: 3px solid ${GOLD}; background: ${SHADE}; padding: 8px 12px;
                    margin: 10px 0; page-break-inside: avoid;
                    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .calloutTitle { font-weight: 700; margin-bottom: 4px; }
  .eng { page-break-inside: avoid; margin-bottom: 14px; }
  .conf { border: 1px solid ${GOLD}; padding: 8px 12px; margin: 16px 0; font-size: 9pt; }
  .foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid ${RULE};
          font-size: 8.5pt; color: ${MUT}; }
</style></head>
<body>${parts.join('\n')}</body></html>`;
}
